/**
 * Printing a binder's labels.
 *
 * A vendor sets up the night before a show: the binder is already in the order
 * the cards will sit in, and the labels have to come off the roll in that same
 * order so they can be applied down the page without sorting a pile. So this
 * prints exactly what the binder is showing, in exactly the order it is showing
 * it, filters and sort included.
 *
 * Everything on the label is optional except that it be readable across a
 * table. Vendors differ - some grade, some do not, some want the set so a buyer
 * can tell two Charizards apart - and a 50mm sticker cannot hold all of it at
 * once, so the choice is theirs and it is remembered.
 *
 * The preview is not decoration. It is the same canvas the printer is sent, at
 * the same dot resolution, so what is on screen is what burns onto the paper.
 * Finding out a name was clipped after a hundred labels is the failure this
 * exists to prevent.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Printer, Bluetooth, Check, X, Loader2, AlertTriangle } from 'lucide-react';

import BottomSheet from '../BottomSheet';
import haptics from '../../utils/haptics';
import {
  LABEL_FIELDS, LABEL_SIZES, renderLabel,
  renderLabelStack, cardsPerPrint,
  connect, disconnect, isConnected, printerModel,
  printingSupported, printingTransport, rememberedPrinter, forgetPrinter,
  readPrefs, writePrefs, sizeForKey,
} from '../../services/labelPrinter';

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {function} props.onClose
 * @param {object[]} props.cards   the binder's cards, already in display order
 * @param {string} props.binderName
 * @param {object} props.theme
 * @param {function} [props.qrUrlFor] (card) => url for the QR field
 */
export default function PrintLabelsSheet({ open, onClose, cards, binderName, theme, qrUrlFor }) {
  // A draft, not the setting itself. Changes used to save on every tap, which
  // left no way back from a chip somebody tried and did not want. Now the
  // sheet ends in Cancel or Save settings, and Cancel means what it says.
  const [prefs, setPrefs] = useState(readPrefs);
  const [linking, setLinking] = useState(false);
  const [linked, setLinked] = useState(() => isConnected());
  const [model, setModel] = useState(() => printerModel());
  const [error, setError] = useState(null);
  const previewRef = useRef(null);

  // What was saved when this sheet opened, so Save can say whether there is
  // anything to save. Read on open rather than once at mount: the sheet stays
  // mounted between visits, and a baseline taken at mount would still be the
  // one from before the last save.
  const [saved, setSaved] = useState(readPrefs);

  // Reopening starts from what is saved, so a cancelled draft is not still
  // sitting there the next time.
  useEffect(() => {
    if (!open) return;
    const stored = readPrefs();
    setSaved(stored);
    setPrefs(stored);
    setError(null);
  }, [open]);

  const size = useMemo(() => sizeForKey(prefs.sizeKey), [prefs.sizeKey]);

  const dirty = prefs.sizeKey !== saved.sizeKey
    || prefs.fields.join(',') !== saved.fields.join(',');

  const remembered = rememberedPrinter();
  const list = useMemo(() => (cards || []).filter(Boolean), [cards]);

  const save = useCallback(() => {
    haptics.tap();
    writePrefs(prefs);
    onClose();
  }, [prefs, onClose]);

  // The preview redraws whenever anything about the label changes, off the
  // FIRST card in the binder, because that is the one coming out of the printer
  // first and the one whose name is most likely to be long.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const sample = list[0];
    if (!sample) return undefined;
    // A paired size previews both halves with two real cards, because the whole
    // point of it is that the two are different. With only one card selected
    // the lower half shows blank, which is exactly what will come out.
    const perPrint = cardsPerPrint(size);
    const draw = perPrint > 1
      ? renderLabelStack(list.slice(0, perPrint), { fields: prefs.fields, size, qrUrlFor })
      : renderLabel(sample, { fields: prefs.fields, size, qrUrl: qrUrlFor ? qrUrlFor(sample) : null });
    draw
      .then((canvas) => {
        if (!alive || !previewRef.current) return;
        const holder = previewRef.current;
        holder.innerHTML = '';
        // Shown at half its dot size, which on a phone is close to the real
        // thing held at arm's length.
        canvas.style.width = `${canvas.width / 2}px`;
        canvas.style.height = `${canvas.height / 2}px`;
        canvas.style.borderRadius = '4px';
        canvas.style.display = 'block';
        holder.appendChild(canvas);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [open, list, prefs.fields, size, qrUrlFor]);

  const toggle = useCallback((key) => {
    haptics.tap();
    setPrefs((p) => ({
      ...p,
      fields: p.fields.includes(key) ? p.fields.filter((k) => k !== key) : [...p.fields, key],
    }));
  }, []);

  const link = useCallback(async ({ pickAgain = false } = {}) => {
    setError(null);
    setLinking(true);
    try {
      await connect({ pickAgain });
      setLinked(true);
      setModel(printerModel());
      haptics.success();
    } catch (err) {
      setError(err?.message || 'Could not reach the printer.');
      setLinked(false);
    } finally {
      setLinking(false);
    }
  }, []);

  const unlink = useCallback(async () => {
    await disconnect();
    forgetPrinter();
    setLinked(false);
    setModel(null);
  }, []);

  const s = makeStyles(theme);
  const supported = printingSupported();

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      label="Print labels"
      overlayStyle={s.overlay}
      sheetStyle={s.sheet}
    >
      <div style={s.wrap}>
        <div style={s.head}>
          <Printer size={18} color={theme.accent} />
          <div style={s.title}>Print labels</div>
          <button style={s.close} onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        <div style={s.sub}>
          {list.length} {list.length === 1 ? 'card' : 'cards'} from {binderName || 'this binder'}, in the
          order you are looking at them.
        </div>

        {!supported && (
          <div style={s.warn}>
            <AlertTriangle size={14} />
            <span>
              This browser has no Bluetooth. Use the app on your phone, or open this in Chrome
              on a computer.
            </span>
          </div>
        )}

        {/* ---- the printer ---- */}
        <div style={s.section}>
          <div style={s.sectionTitle}>Printer</div>
          <div style={s.printerRow}>
            <Bluetooth size={16} color={linked ? theme.success : theme.textMuted} />
            <div style={s.printerText}>
              <div style={s.printerName}>
                {linked ? (model || 'Connected') : remembered ? remembered.name || 'Last printer' : 'No printer linked'}
              </div>
              <div style={s.printerHint}>
                {linked
                  ? 'Ready'
                  : remembered
                    ? 'Tap connect, and make sure the NIIMBOT app is closed'
                    : 'Turn the printer on, then tap link'}
              </div>
            </div>
            {linking ? (
              <Loader2 size={18} style={s.spin} />
            ) : linked ? (
              <button style={s.ghostBtn} onClick={unlink}>Forget</button>
            ) : (
              <button
                style={s.ghostBtn}
                onClick={() => link({ pickAgain: !remembered || printingTransport() === 'web' })}
                disabled={!supported}
              >
                {remembered && printingTransport() !== 'web' ? 'Connect' : 'Link printer'}
              </button>
            )}
          </div>
          {/* The picker cannot name these printers - they advertise neither a
              name nor their service - so it lists every radio in the room and
              the printer is one of the unnamed rows. Saying so beats letting
              somebody guess at it. */}
          {!linked && !linking && supported && (
            <div style={s.pickerHint}>
              The picker lists every Bluetooth device nearby, and these printers
              broadcast no name. Turn the printer on last and it is usually the
              newest unnamed row.
            </div>
          )}
        </div>

        {/* ---- label size ---- */}
        <div style={s.section}>
          <div style={s.sectionTitle}>Label size</div>
          <div style={s.chips}>
            {LABEL_SIZES.map((o) => (
              <button
                key={o.key}
                style={{ ...s.chip, ...(o.key === size.key ? s.chipOn : null) }}
                onClick={() => { haptics.tap(); setPrefs((p) => ({ ...p, sizeKey: o.key })); }}
              >
                {o.label}
                {o.hint ? <span style={s.chipHint}>{o.hint}</span> : null}
              </button>
            ))}
          </div>
        </div>

        {/* ---- what goes on it ---- */}
        <div style={s.section}>
          <div style={s.sectionTitle}>What goes on the label</div>
          {LABEL_FIELDS.map((f) => {
            const on = prefs.fields.includes(f.key);
            return (
              <button key={f.key} style={s.fieldRow} onClick={() => toggle(f.key)}>
                <div style={{ ...s.box, ...(on ? s.boxOn : null) }}>
                  {on && <Check size={13} color="#04263d" strokeWidth={3} />}
                </div>
                <div style={s.fieldText}>
                  <div style={s.fieldLabel}>{f.label}</div>
                  <div style={s.fieldHint}>{f.hint}</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* ---- what it will look like ---- */}
        <div style={s.section}>
          <div style={s.sectionTitle}>Preview · {list[0]?.name || 'first card'}</div>
          <div style={s.previewFrame}><div ref={previewRef} /></div>
        </div>

        {error && <div style={s.error}>{error}</div>}

        {/* Settings, not a second way to print. Printing runs from the screen
            before this one, which is the only place that knows what the
            rounding pass decided each price should be. */}
        <div style={s.actions}>
          <button style={s.cancelBtn} onClick={onClose}>Cancel</button>
          <button
            style={{ ...s.saveBtn, ...(dirty ? null : s.saveBtnOff) }}
            onClick={save}
          >
            <Check size={16} />
            {dirty ? 'Save settings' : 'Saved'}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

const makeStyles = (theme) => ({
  // BottomSheet draws no styles of its own - every caller brings the scrim and
  // the panel. Without these the sheet renders as a plain block below the fold,
  // present in the page and invisible on screen, which is exactly what it did.
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.62)',
    zIndex: 10004,
  },
  sheet: {
    position: 'fixed',
    bottom: 0, left: 0, right: 0,
    margin: '0 auto',
    // On a phone this is the full width. On a desktop, where a vendor might set
    // up a show from a laptop, a sheet stretched across a 1680px window is not
    // a sheet.
    maxWidth: '560px',
    borderRadius: '20px 20px 0 0',
    backgroundColor: theme.bgSheet || theme.bgCard,
    zIndex: 10005,
    maxHeight: '86vh',
    overflowY: 'auto',
    paddingBottom: 'var(--safe-area-bottom, 0px)',
  },
  wrap: { padding: '14px 18px 26px', color: theme.text },
  head: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 },
  title: { fontSize: 18, fontWeight: '800', flex: 1 },
  close: {
    width: 32, height: 32, borderRadius: 16, border: 'none',
    backgroundColor: 'transparent', color: theme.textMuted,
    display: 'grid', placeItems: 'center', cursor: 'pointer', padding: 0,
  },
  sub: { fontSize: 13, color: theme.textSecondary, lineHeight: 1.5, marginBottom: 16 },
  warn: {
    display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px',
    borderRadius: 10, backgroundColor: 'rgba(245,158,11,0.12)',
    border: '1px solid rgba(245,158,11,0.34)', color: '#f59e0b',
    fontSize: 12, lineHeight: 1.45, marginBottom: 14,
  },
  section: { marginBottom: 18 },
  sectionTitle: {
    fontSize: 11, fontWeight: '800', letterSpacing: '0.06em', textTransform: 'uppercase',
    color: theme.textMuted, marginBottom: 8,
  },
  printerRow: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
    borderRadius: 12, backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`,
  },
  printerText: { flex: 1, minWidth: 0 },
  printerName: { fontSize: 14, fontWeight: '700' },
  printerHint: { fontSize: 11, color: theme.textSecondary, marginTop: 2, lineHeight: 1.4 },
  ghostBtn: {
    padding: '8px 14px', borderRadius: 9, backgroundColor: 'transparent',
    border: `1px solid ${theme.border}`, color: theme.text,
    fontSize: 13, fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  chips: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  chip: {
    padding: '8px 14px', borderRadius: 999, backgroundColor: 'transparent',
    border: `1px solid ${theme.border}`, color: theme.textSecondary,
    fontSize: 13, fontWeight: '600', cursor: 'pointer',
  },
  // border, not borderColor. The base style sets the shorthand, and a longhand
  // override wins over the colour inside it while leaving its width and style
  // alone - which is how a border ends up half from one rule and half from
  // another and nobody can see why.
  chipOn: {
    border: `1px solid ${theme.accent}`,
    color: theme.accent,
    backgroundColor: 'rgba(31,155,240,0.12)',
  },
  chipHint: { display: 'block', fontSize: 9, fontWeight: '600', opacity: 0.7, marginTop: 1 },
  fieldRow: {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
    padding: '10px 2px', background: 'none', border: 'none',
    textAlign: 'left', cursor: 'pointer', color: 'inherit',
  },
  box: {
    width: 22, height: 22, borderRadius: 6, flex: '0 0 auto',
    border: `2px solid ${theme.border}`, display: 'grid', placeItems: 'center',
  },
  boxOn: { backgroundColor: theme.accent, borderColor: theme.accent },
  fieldText: { minWidth: 0 },
  fieldLabel: { fontSize: 14, fontWeight: '600' },
  fieldHint: { fontSize: 11, color: theme.textMuted, marginTop: 1 },
  previewFrame: {
    display: 'flex', justifyContent: 'center', padding: 14,
    borderRadius: 12, backgroundColor: '#d8d8d8',
    border: `1px solid ${theme.border}`,
  },
  error: {
    fontSize: 13, color: '#f87171', lineHeight: 1.45, marginBottom: 12,
    padding: '10px 12px', borderRadius: 10,
    backgroundColor: 'rgba(248,113,113,0.10)', border: '1px solid rgba(248,113,113,0.3)',
  },
  actions: { marginTop: 4, display: 'flex', gap: 10 },
  cancelBtn: {
    flex: '0 0 auto', padding: '15px 22px', borderRadius: 12,
    backgroundColor: 'transparent', border: `1px solid ${theme.border}`,
    color: theme.textSecondary, fontSize: 15, fontWeight: '600', cursor: 'pointer',
  },
  saveBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    flex: 1, padding: '15px', borderRadius: 12, border: 'none',
    backgroundColor: '#1f9bf0', color: '#04263d',
    fontSize: 15, fontWeight: '700', cursor: 'pointer',
  },
  // Still a button with nothing to save: tapping it closes the sheet, which is
  // what somebody who has changed nothing wants anyway.
  saveBtnOff: { opacity: 0.45 },
  pickerHint: {
    marginTop: 10, fontSize: 11.5, lineHeight: 1.45, color: theme.textMuted,
  },
  spin: { animation: 'cc-spin 1s linear infinite', color: theme.textMuted },
});
